import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { ArrowLeft, User, Calendar, Edit, Eye, Coins, Image, Settings } from 'lucide-react';

const CharacterDetail = ({ characterId }) => {
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mystveil_token'));

  useEffect(() => {
    const loadCharacter = async () => {
      if (!characterId) {
        setError('Character ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const headers = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`https://api.4fingerstudios.com/api/mystveil/characters/${characterId}`, {
          headers
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError('Character not found');
          } else if (response.status === 401) {
            setError('Please log in to view character details');
          } else {
            throw new Error(`Failed to load character: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        setCharacter(data);
      } catch (err) {
        setError('Failed to load character details');
        console.error('Character load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCharacter();
  }, [characterId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-50">
        <MysticalParticles />
        <Navbar />
        <main className="flex-grow px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mystical-card">
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="mystical-text text-gray-600">Loading character details...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-50">
        <MysticalParticles />
        <Navbar />
        <main className="flex-grow px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mystical-card">
              <div className="text-center py-12">
                <div className="text-red-500 text-6xl mb-4">⚠️</div>
                <h2 className="mystical-title text-black text-2xl mb-4">Error</h2>
                <p className="mystical-text text-gray-600 mb-6">{error}</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="/characters" className="mystical-button">
                    <ArrowLeft className="mr-2" size={20} />
                    Back to Characters
                  </Link>
                  <Link to="/profile" className="mystical-button">
                    <User className="mr-2" size={20} />
                    My Profile
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!character) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-50">
        <MysticalParticles />
        <Navbar />
        <main className="flex-grow px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mystical-card">
              <div className="text-center py-12">
                <div className="text-gray-500 text-6xl mb-4">❓</div>
                <h2 className="mystical-title text-black text-2xl mb-4">Character Not Found</h2>
                <p className="mystical-text text-gray-600 mb-6">This character doesn't exist or has been removed.</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="/characters" className="mystical-button">
                    <ArrowLeft className="mr-2" size={20} />
                    Back to Characters
                  </Link>
                  <Link to="/profile" className="mystical-button">
                    <User className="mr-2" size={20} />
                    My Profile
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-50">
      <MysticalParticles />
      <Navbar />
      
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mystical-card mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <Link to="/characters" className="mystical-button">
                  <ArrowLeft className="mr-2" size={20} />
                  Back to Characters
                </Link>
                <h1 className="mystical-title text-black">
                  Character Details
                </h1>
              </div>
              {token && (
                <div className="flex space-x-2">
                  <Link to={`/characters/${characterId}/edit`} className="mystical-button">
                    <Edit className="mr-2" size={20} />
                    Edit Character
                  </Link>
                  <Link to={`/upgrades/${characterId}`} className="mystical-button">
                    <Settings className="mr-2" size={20} />
                    View Upgrades
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Character Information */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Character Image and Basic Info */}
            <div className="lg:col-span-1">
              <div className="mystical-card">
                <div className="text-center">
                  {character.characterImage ? (
                    <img 
                      src={character.characterImage} 
                      alt={character.characterName}
                      className="w-full h-64 object-cover rounded-lg mb-4"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center mb-4">
                      <Image className="text-gray-400" size={64} />
                    </div>
                  )}
                  
                  <h2 className="mystical-title text-black text-2xl mb-2">
                    {character.characterName}
                  </h2>
                  
                  {character.characterDescription && (
                    <p className="mystical-text text-gray-700 mb-4">
                      {character.characterDescription}
                    </p>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="mystical-text text-gray-600">Owner:</span>
                      <span className="mystical-text font-bold text-purple-800">
                        {character.username || 'Unknown'}
                      </span>
                    </div>
                    
                    {character.createdAt && (
                      <div className="flex items-center justify-between">
                        <span className="mystical-text text-gray-600">Created:</span>
                        <span className="mystical-text text-gray-700">
                          {new Date(character.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {character.updatedAt && (
                      <div className="flex items-center justify-between">
                        <span className="mystical-text text-gray-600">Last Updated:</span>
                        <span className="mystical-text text-gray-700">
                          {new Date(character.updatedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Character Stats and Details */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {/* Character Stats */}
                <div className="mystical-card">
                  <h3 className="mystical-subtitle text-black text-xl mb-4">
                    Character Statistics
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white bg-opacity-95 p-4 rounded-lg">
                      <div className="flex items-center space-x-3 mb-2">
                        <Coins className="text-purple-800" size={24} />
                        <h4 className="mystical-subtitle text-black">Total Chips</h4>
                      </div>
                      <p className="mystical-text text-2xl font-bold text-purple-800">
                        {character.totalChips || 0}
                      </p>
                    </div>

                    <div className="bg-white bg-opacity-95 p-4 rounded-lg">
                      <div className="flex items-center space-x-3 mb-2">
                        <Image className="text-purple-800" size={24} />
                        <h4 className="mystical-subtitle text-black">Art Submissions</h4>
                      </div>
                      <p className="mystical-text text-2xl font-bold text-purple-800">
                        {character.artSubmissions || 0}
                      </p>
                    </div>

                    <div className="bg-white bg-opacity-95 p-4 rounded-lg">
                      <div className="flex items-center space-x-3 mb-2">
                        <Settings className="text-purple-800" size={24} />
                        <h4 className="mystical-subtitle text-black">Upgrades</h4>
                      </div>
                      <p className="mystical-text text-2xl font-bold text-purple-800">
                        {character.upgrades || 0}
                      </p>
                    </div>

                    <div className="bg-white bg-opacity-95 p-4 rounded-lg">
                      <div className="flex items-center space-x-3 mb-2">
                        <Calendar className="text-purple-800" size={24} />
                        <h4 className="mystical-subtitle text-black">Age</h4>
                      </div>
                      <p className="mystical-text text-2xl font-bold text-purple-800">
                        {character.age || 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Character Traits */}
                {character.traits && character.traits.length > 0 && (
                  <div className="mystical-card">
                    <h3 className="mystical-subtitle text-black text-xl mb-4">
                      Character Traits
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {character.traits.map((trait, index) => (
                        <span 
                          key={index}
                          className="bg-purple-800 text-white px-3 py-1 rounded-full text-sm font-bold"
                        >
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Character Backstory */}
                {character.backstory && (
                  <div className="mystical-card">
                    <h3 className="mystical-subtitle text-black text-xl mb-4">
                      Backstory
                    </h3>
                    <p className="mystical-text text-gray-700 leading-relaxed">
                      {character.backstory}
                    </p>
                  </div>
                )}

                {/* Recent Activity */}
                {character.recentActivity && character.recentActivity.length > 0 && (
                  <div className="mystical-card">
                    <h3 className="mystical-subtitle text-black text-xl mb-4">
                      Recent Activity
                    </h3>
                    <div className="space-y-3">
                      {character.recentActivity.map((activity, index) => (
                        <div key={index} className="bg-white bg-opacity-95 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <p className="mystical-text text-gray-700">
                              {activity.description}
                            </p>
                            <span className="mystical-text text-sm text-gray-500">
                              {new Date(activity.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mystical-card mt-8">
            <h3 className="mystical-subtitle text-black text-xl mb-6">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to={`/upgrades/${characterId}`} className="mystical-button">
                <Settings className="mr-2" size={20} />
                View Upgrades
              </Link>
              <Link to={`/gallery/character/${characterId}`} className="mystical-button">
                <Image className="mr-2" size={20} />
                View Gallery
              </Link>
              <Link to="/submit" className="mystical-button">
                <Image className="mr-2" size={20} />
                Submit Artwork
              </Link>
              <Link to="/characters" className="mystical-button">
                <Eye className="mr-2" size={20} />
                Browse Characters
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/profile" className="mystical-button">
              <User className="mr-2" size={20} />
              My Profile
            </Link>
            <Link to="/characters" className="mystical-button">
              <Eye className="mr-2" size={20} />
              All Characters
            </Link>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CharacterDetail;
